import { Button, Modal, NumberInput, Stack, Text } from '@mantine/core'
import { useMemo, useState } from 'react'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { formatNumberToCurrency, isFundManagerEntity } from 'app/utils/util-functions'
import { useDispatch, useSelector } from 'react-redux'
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import { useForm } from '@mantine/form'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { getFacilities, getLockedFacilities } from 'app/state/ducks/facilities/selectors'
import { TradedFacilityParams } from 'app/models/traded-facilities-params'
import { editTradeAssets } from 'app/state/ducks/master-trades/thunks'
import { loadAllSubTrades } from 'app/state/ducks/sub-trades/thunks'
import { loadTradedFacilities } from 'app/state/ducks/traded-facilities/thunks'
import WarningText from 'app/views/components/warning-text/warning-text'
import { TradeEntryDealSearch } from '../trade-entry-creation/trade-entry-deal-section/trade-entry-deal-search'

type MultiPartFormProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  trade?: MasterTradeParams
  tradedFacilities: TradedFacilityParams[]
}

export default function EditTradeAssetsModal({ open, setOpen, trade, tradedFacilities }: MultiPartFormProps) {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const deals = useSelector(getDeals)
  const subTrades = useSelector(getSubTrades)
  const entities = useSelector(getEntities)
  const entityProfiles = useSelector(getEntityProfiles)
  const facilities = useSelector(getFacilities)
  const lockedFacilities = useSelector(getLockedFacilities)
  const allFacilities = useMemo(() => [...facilities, ...lockedFacilities], [facilities, lockedFacilities]);


  const dealFacilities = allFacilities.filter((facility: any) => facility.dealId.id === trade?.dealId?.id)
  const facilityValues = dealFacilities.map((facility: any) => {
    const tradedFacility = tradedFacilities.find(tf => tf.facilityId.id === facility.id)
    return {
      facilityId: facility.id,
      tradeAmount: Number(tradedFacility?.tradeAmount ?? 0.0),
      price: Number(tradedFacility?.price ?? 0.0),
      facility: facility,
    }
  }).filter((facilityValue: any) => {
    // Filter out locked facilities with both price and tradeAmount as 0
    const isLocked = lockedFacilities.some((lf: any) => lf.id === facilityValue.facilityId)
    if (isLocked && facilityValue.tradeAmount === 0.0 && facilityValue.price === 0.0) {
      return false
    }
    return true
  }).sort((a, b) => {
    const nameA = a.facility?.name?.toLowerCase() || ''
    const nameB = b.facility?.name?.toLowerCase() || ''
    return nameA.localeCompare(nameB)
  })

  const form = useForm({
    initialValues: {
      dealId: trade?.dealId?.id ?? "",
      tradedFacilities: facilityValues,
    },
    transformValues: (values: { dealId: string, tradedFacilities: any[] }) => {
      const deal = deals.find((deal: any) => deal.id === values.dealId)
      const tradedFacilities = values.tradedFacilities.map((tf: any) => {
        const facility = allFacilities.find((f: any) => f.id === tf.facilityId);
        return {
          facilityId: { id: facility?.id ?? '', admin: facility?.accountManagementAdmin ?? '' },
          tradeAmount: tf.tradeAmount,
          price: tf.price,
          facilityAmount: '' + (facility?.amount ?? '0.0'),
          facilityDetails: null,
          fees: [],
          facility,
        }
      })
      return {
        dealId: { id: deal?.id ?? '', admin: deal?.accountManagementAdmin ?? '' },
        tradedFacilities: tradedFacilities

      }
    }
  })

  const isAllocated = useMemo(() => {
    return subTrades.some(st => st.masterTradeId.id === trade?.id)
  }, [subTrades])

  const isFMIncluded = useMemo(() => {
    return isFundManagerEntity(trade?.buyerId?.id, entities, entityProfiles)
      || isFundManagerEntity(trade?.sellerId?.id, entities, entityProfiles)
  }, [trade, entities, entityProfiles])

  const isSettled = useMemo(() => {
    return subTrades.some(st => st.masterTradeId.id === trade?.id && st.status == "Settled")
  }, [subTrades])

  const settledFacilityIds = useMemo(() => {
    const settledSubTrades = subTrades.filter(st => st.masterTradeId.id === trade?.id && st.status == "Settled")
    return settledSubTrades.flatMap((st) => st.facilityIds).map(facilityId => facilityId.id)
  }, [subTrades])

  const warningMessage = useMemo(() => {
    if (!isFMIncluded && isSettled)
      return "Can’t edit because one or more SubTrades are Settled."
    if (settledFacilityIds && settledFacilityIds.length > 0)
      return "Can’t edit Traded Deal and price for facilities with Settled SubTrades."
    if (isFMIncluded && isAllocated)
      return "Cannot edit Traded Deal since subtrades are not settled"
    return null
  }, [isAllocated, isFMIncluded, isSettled, settledFacilityIds])

  const onDealChange = (value?: string | null) => {
    if (!value) {
      form.setFieldValue('dealId', '')
      form.setFieldValue('tradedFacilities', [])
      return
    }
    const deal = deals.find((deal: any) => deal.id === value)
    if (!deal) {
      form.setFieldValue('dealId', '')
      form.setFieldValue('tradedFacilities', [])
      return
    }

    form.setFieldValue('dealId', deal.id)

    const dealFacilities = allFacilities.filter((facility: any) => facility.dealId.id === value)
    const facilityValues = dealFacilities.map((facility: any) => {
      const tradedFacility = tradedFacilities.find(tf => tf.facilityId.id === facility.id)

      return {
        facilityId: facility.id,
        tradeAmount: Number(tradedFacility?.tradeAmount ?? 0.0),
        price: Number(tradedFacility?.price ?? 0.0),
        facility: facility,
      }
    }).filter((facilityValue: any) => {
      // Filter out locked facilities with both price and tradeAmount as 0
      const isLocked = lockedFacilities.some((lf: any) => lf.id === facilityValue.facilityId)
      if (isLocked && facilityValue.tradeAmount === 0.0 && facilityValue.price === 0.0) {
        return false
      }
      return true
    }).sort((a, b) => {
      const nameA = a.facility?.name?.toLowerCase() || ''
      const nameB = b.facility?.name?.toLowerCase() || ''
      return nameA.localeCompare(nameB)
    })
    form.setFieldValue('tradedFacilities', facilityValues)
  }

  async function submitEvent() {
    const assetsData = form.getTransformedValues()
    try {
      setIsLoading(true)
      const response: any = await dispatch(editTradeAssets(assetsData as any, trade))
      if (response.success) {
        SuccessNotification({
          title: 'Successful Trade assets updated',
          message: 'You edited an Trade assets for open Trade',
        })
      } else {
        ErrorNotification({
          title: 'Trade assets save failed',
          message: response.payload ?? 'Check data and try again',
        })
        return
      }
      dispatch(loadAllSubTrades())
      dispatch(loadTradedFacilities())
      setOpen(false)
    } catch {
      ErrorNotification({
        title: 'Trade assets save failed',
        message: 'Check data and try again',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      className="modal-body create-new-form fit-content-modal"
      opened={open}
      onClose={() => setOpen(!open)}
      centered={true}
      size="77%"
    >
      <form onSubmit={form.onSubmit(() => { submitEvent() })}>
        <Stack p="20px" spacing="20px !important">
          <Text className='edit-trade-details-title'>Edit Trade</Text>
          <Text className='edit-trade-details-sub-title'>Trade Assets</Text>
          <Stack
            w="100%"
            h="100%"
            spacing="12px !important"
            p="20px"
          >
            <TradeEntryDealSearch loading={isLoading} onChange={onDealChange} value={form.values.dealId} />

            <table className="edit-assets-facility-table">
              <thead>
                <tr>
                  <th>Facility Name</th>
                  <th>Facility Type</th>
                  <th style={{ borderBottom: "1px solid #71c0ee" }}>Traded Amount</th>
                  <th style={{ borderBottom: "1px solid #71c0ee" }}>Price</th>
                  <th>CUSIP</th>
                  <th>LX ID</th>
                  <th>Facility ID</th>
                  <th>Client Facility ID</th>
                </tr>
              </thead>
              <tbody>
                {form.values.tradedFacilities.map((tradeFacility: any, index: number) => {
                  return (
                    <tr key={index}>
                      <td className='facility-name-cell'>{tradeFacility.facility.name ?? ''}</td>
                      <td className='facility-name-cell'>{tradeFacility.facility.facilityType ?? ''}</td>
                      <td className='assets-input-cell'>
                        <NumberInput
                          disabled={isLoading}
                          precision={2}
                          parser={value => value.replace(/[^0-9.]/g, '')}
                          formatter={value =>
                            !Number.isNaN(parseFloat(value))
                              ? formatNumberToCurrency(Number(
                                parseFloat(
                                  value.replace(
                                    /\B(?<!\d*)(?=(\d{3})+(?!\d))/g,
                                    ','
                                  )
                                )
                              ), tradeFacility.facility?.baseCurrency ?? 'USD')
                              : '$ 0'
                          }
                          {...form.getInputProps(`tradedFacilities.${index}.tradeAmount`)}
                        />
                      </td>
                      <td className='assets-input-cell'>
                        <NumberInput
                          disabled={isLoading || settledFacilityIds.includes(tradeFacility.facilityId)}
                          precision={10}
                          parser={value =>
                            value ? value.replace(/%\s?|\$\s?|(,*)/g, '') : ''
                          }
                          formatter={value =>
                            !Number.isNaN(parseFloat(value ?? '')) ? value + ' %' : '%'
                          }
                          {...form.getInputProps(`tradedFacilities.${index}.price`)}
                        /></td>
                      <td className='facility-name-cell'>{tradeFacility.facility.cusip}</td>
                      <td className='facility-name-cell'>{tradeFacility.facility.lxId}</td>
                      <td className='facility-name-cell'>{tradeFacility.facility.systemId}</td>
                      <td className='facility-name-cell'>{tradeFacility.facility.clientId}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table >
            {warningMessage && <WarningText text={warningMessage} />}
            <div className="action-buttons-block">
              <Button className="cancel-button" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                loading={isLoading}
                disabled={!form.isValid()}
                className="delete-button stratch-delete-button"
              >
                Save
              </Button>
            </div>
          </Stack>
        </Stack>
      </form>
    </Modal >
  )
}